import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { mainSiteUrl, categories } from "@/mocks/categories";

type NavItem =
  | { key: string; type: "link"; to: string }
  | { key: string; type: "section"; id: string };

const navItems: NavItem[] = [
  { key: "nav.home", type: "link", to: "/" },
  { key: "nav.shoppingGuide", type: "link", to: "/article/allchinabuy-spreadsheet-guide" },
  { key: "nav.spreadsheet", type: "section", id: "spreadsheet" },
  { key: "nav.products", type: "section", id: "categories" },
  { key: "nav.guides", type: "section", id: "articles" },
  { key: "nav.safety", type: "section", id: "safety" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 20);
    }
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function goToSection(id: string) {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    navigate("/");
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 120);
  }

  const solid = scrolled || mobileOpen;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
        solid
          ? "bg-background-50 border-b border-background-200"
          : "bg-gradient-to-b from-black/30 to-transparent"
      }`}
    >
      <nav className="mx-auto flex items-center justify-between px-4 md:px-6 h-16 max-w-[1280px]">
        <Link
          to="/"
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setMobileOpen(false)}
        >
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-table-line" />
          </span>
          <span
            className={`font-heading font-semibold text-lg whitespace-nowrap ${
              solid ? "text-foreground-950" : "text-background-50"
            }`}
          >
            AllChinaBuy Spreadsheet
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item) => {
            const base =
              "px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer transition-colors";
            const color = solid
              ? "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
              : "text-background-50 hover:bg-background-50/20";
            if (item.type === "link") {
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={`${base} ${color}`}
                >
                  {t(item.key)}
                </Link>
              );
            }
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => goToSection(item.id)}
                className={`${base} ${color}`}
              >
                {t(item.key)}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher light={!solid} />
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            {t("nav.cta")}
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line" />
            </span>
          </a>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
              solid ? "text-foreground-800 hover:bg-background-100" : "text-background-50 hover:bg-background-50/20"
            }`}
            aria-label="Menu"
          >
            <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3 max-h-[75vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            {navItems.map((item) => {
              if (item.type === "link") {
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="px-3 py-2.5 text-left text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-md whitespace-nowrap cursor-pointer"
                  >
                    {t(item.key)}
                  </Link>
                );
              }
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => goToSection(item.id)}
                  className="px-3 py-2.5 text-left text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-md whitespace-nowrap cursor-pointer"
                >
                  {t(item.key)}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => setCatsOpen((v) => !v)}
              className="mt-1 flex items-center justify-between px-3 py-2.5 text-left text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer"
              aria-expanded={catsOpen}
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                  <i className="ri-layout-grid-line" />
                </span>
                {t("nav.categories")}
              </span>
              <span className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line transition-transform ${
                    catsOpen ? "rotate-180" : ""
                  }`}
                />
              </span>
            </button>

            {catsOpen && (
              <div className="grid grid-cols-2 gap-1 rounded-md bg-background-100/60 p-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.href}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-2.5 py-2 text-sm text-foreground-700 hover:bg-background-50 rounded-md cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-primary-600 shrink-0">
                      <i className={cat.icon} />
                    </span>
                    <span className="truncate">{t(cat.nameKey)}</span>
                  </a>
                ))}
              </div>
            )}

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}